import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import "../../styles/settings/geofence.css";
import { leafletLayerToGeoJson, polygonBounds } from "../../utils/geofence";

const TILE_URL =
  "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';

const POLYGON_STYLE = {
  className: "geofence-polygon",
  color: "#8a1125",
  weight: 2.5,
  fillColor: "#8a1125",
  fillOpacity: 0.14,
};

// Simple polygon area — spherical excess isn't overkill for the scale of a
// site. Uses leaflet-draw's bundled GeometryUtil when available, else falls
// back to a planar-meters approximation good enough for UI display.
const computeAreaM2 = (latlngs) => {
  if (!Array.isArray(latlngs) || latlngs.length < 3) return 0;
  if (L.GeometryUtil?.geodesicArea) {
    return L.GeometryUtil.geodesicArea(latlngs);
  }
  return 0;
};

const formatArea = (m2) => {
  if (!m2 || m2 <= 0) return "—";
  if (m2 < 10_000) return `${Math.round(m2).toLocaleString("sv-SE")} m²`;
  const ha = m2 / 10_000;
  return `${ha.toFixed(ha >= 10 ? 0 : 1)} ha`;
};

/**
 * Interactive geofence editor with a guided HUD.
 *
 * Props:
 *   - center:   { lat, lng } | null
 *   - polygon:  GeoJSON Polygon | null
 *   - onChange: (geojson | null) => void
 */
export default function GeofenceDrawMap({ center, polygon, onChange }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const drawLayerRef = useRef(null);
  const drawHandlerRef = useRef(null);
  const lastPolygonRef = useRef(null);
  const onChangeRef = useRef(onChange);

  const [drawing, setDrawing] = useState(false);
  const [vertexCount, setVertexCount] = useState(0);
  const [areaM2, setAreaM2] = useState(0);
  const [hasPolygon, setHasPolygon] = useState(false);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Read current polygon area+vertex from drawn layers.
  const refreshStats = useCallback(() => {
    const drawn = drawLayerRef.current;
    if (!drawn) return;
    const layers = drawn.getLayers();
    if (layers.length === 0) {
      setVertexCount(0);
      setAreaM2(0);
      setHasPolygon(false);
      return;
    }
    const ring = layers[0].getLatLngs()[0];
    setVertexCount(ring.length);
    setAreaM2(computeAreaM2(ring));
    setHasPolygon(true);
  }, []);

  const emitChange = useCallback(() => {
    const drawn = drawLayerRef.current;
    if (!drawn) return;
    const layers = drawn.getLayers();
    if (layers.length === 0) {
      lastPolygonRef.current = null;
      onChangeRef.current?.(null);
      refreshStats();
      return;
    }
    const geo = leafletLayerToGeoJson(layers[0]);
    lastPolygonRef.current = geo;
    onChangeRef.current?.(geo);
    refreshStats();
  }, [refreshStats]);

  // Init
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      zoomControl: true,
      preferCanvas: true,
      worldCopyJump: true,
      center: [59.3293, 18.0686],
      zoom: 12,
      zoomSnap: 0.5,
    });

    L.control.zoom({ position: "bottomright" });

    L.tileLayer(TILE_URL, {
      attribution: TILE_ATTRIBUTION,
      subdomains: "abcd",
      maxZoom: 19,
      detectRetina: true,
    }).addTo(map);

    const drawnItems = new L.FeatureGroup().addTo(map);
    drawLayerRef.current = drawnItems;

    // Create the draw control so its CSS handles the in-progress guide line,
    // but the toolbar itself is hidden via CSS and we drive the handler
    // programmatically below.
    const drawControl = new L.Control.Draw({
      edit: { featureGroup: drawnItems, remove: false, edit: false },
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
          shapeOptions: POLYGON_STYLE,
        },
        polyline: false,
        rectangle: false,
        circle: false,
        circlemarker: false,
        marker: false,
      },
    });
    map.addControl(drawControl);

    map.on(L.Draw.Event.CREATED, (e) => {
      drawnItems.clearLayers();
      drawnItems.addLayer(e.layer);
      setDrawing(false);
      emitChange();
    });
    map.on(L.Draw.Event.DRAWSTART, () => setDrawing(true));
    map.on(L.Draw.Event.DRAWSTOP, () => setDrawing(false));
    map.on(L.Draw.Event.DRAWVERTEX, () => {
      // While drawing, the layer is on the map but not yet in drawnItems.
      // Approximate vertex count from the draw handler's internal ring.
      const h = drawHandlerRef.current;
      const markers = h?._markers;
      if (markers) setVertexCount(markers.length);
    });

    mapRef.current = map;

    const resize = () => map.invalidateSize();
    window.addEventListener("resize", resize);
    const t = setTimeout(resize, 60);

    return () => {
      window.removeEventListener("resize", resize);
      clearTimeout(t);
      map.remove();
      mapRef.current = null;
      drawLayerRef.current = null;
      drawHandlerRef.current = null;
    };
  }, [emitChange]);

  // Centre on address
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !center) return;
    map.setView([center.lat, center.lng], 17, { animate: true });
  }, [center]);

  // Hydrate incoming polygon
  useEffect(() => {
    const map = mapRef.current;
    const drawn = drawLayerRef.current;
    if (!map || !drawn) return;
    if (polygon && polygon === lastPolygonRef.current) return;

    drawn.clearLayers();
    if (polygon?.coordinates?.[0]) {
      const latlngs = polygon.coordinates[0]
        .slice(0, -1) // drop closing duplicate
        .map(([lng, lat]) => [lat, lng]);
      if (latlngs.length >= 3) {
        const layer = L.polygon(latlngs, POLYGON_STYLE);
        drawn.addLayer(layer);
        const bounds = polygonBounds(polygon);
        if (bounds) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 18 });
        lastPolygonRef.current = polygon;
      }
    } else {
      lastPolygonRef.current = null;
    }
    refreshStats();
  }, [polygon, refreshStats]);

  const startDrawing = useCallback(() => {
    const map = mapRef.current;
    const drawn = drawLayerRef.current;
    if (!map || !drawn) return;
    // Clear any previous polygon — one area per organisation.
    drawn.clearLayers();
    lastPolygonRef.current = null;
    onChangeRef.current?.(null);

    // Cancel any in-flight handler first.
    drawHandlerRef.current?.disable?.();

    const handler = new L.Draw.Polygon(map, {
      allowIntersection: false,
      showArea: true,
      shapeOptions: POLYGON_STYLE,
      icon: L.divIcon({
        className: "leaflet-div-icon leaflet-editing-icon",
        iconSize: new L.Point(10, 10),
      }),
    });
    handler.enable();
    drawHandlerRef.current = handler;
  }, []);

  const cancelDrawing = useCallback(() => {
    drawHandlerRef.current?.disable?.();
    drawHandlerRef.current = null;
    setDrawing(false);
    refreshStats();
  }, [refreshStats]);

  const finishDrawing = useCallback(() => {
    const handler = drawHandlerRef.current;
    if (!handler) return;
    // completeShape fires DRAW:CREATED which will emit onChange via our handler.
    if (typeof handler.completeShape === "function") {
      handler.completeShape();
    }
  }, []);

  const undoLastPoint = useCallback(() => {
    const handler = drawHandlerRef.current;
    if (handler && typeof handler.deleteLastVertex === "function") {
      handler.deleteLastVertex();
      const markers = handler._markers;
      if (markers) setVertexCount(markers.length);
    }
  }, []);

  const clearPolygon = useCallback(() => {
    const drawn = drawLayerRef.current;
    if (!drawn) return;
    drawn.clearLayers();
    lastPolygonRef.current = null;
    onChangeRef.current?.(null);
    refreshStats();
  }, [refreshStats]);

  const bannerContent = useMemo(() => {
    if (drawing) {
      return {
        title: "Rita området",
        sub: `Klicka för att lägga till hörn. ${
          vertexCount >= 3 ? "Avsluta när du är nöjd." : "Minst 3 punkter krävs."
        }`,
      };
    }
    if (hasPolygon) {
      return {
        title: "Område klart",
        sub: "Tryck Spara i panelen till vänster. Du kan rita om när som helst.",
      };
    }
    if (center) {
      return {
        title: "Adress vald",
        sub: "Tryck Starta ritning för att markera området på kartan.",
      };
    }
    return {
      title: "Kom igång",
      sub: "Sök upp er adress i panelen till vänster — kartan flyttar dit.",
    };
  }, [drawing, hasPolygon, vertexCount, center]);

  const canFinish = drawing && vertexCount >= 3;

  return (
    <div className="geofence-map-shell">
      <div
        className={`geofence-map ${drawing ? "geofence-drawing" : ""}`}
        ref={containerRef}
      />

      <div className="geofence-hud-banner">
        <span className="pulse" />
        <div>
          <div className="banner-title">{bannerContent.title}</div>
          <div className="banner-sub">{bannerContent.sub}</div>
        </div>
      </div>

      {(drawing || hasPolygon) && (
        <div className="geofence-hud-stats" aria-hidden>
          <div className="stat">
            <span className="stat-label">Punkter</span>
            <span className="stat-value">{vertexCount}</span>
          </div>
          <div className="divider" />
          <div className="stat">
            <span className="stat-label">Yta</span>
            <span className="stat-value">{formatArea(areaM2)}</span>
          </div>
        </div>
      )}

      <div className="geofence-hud-actions" role="toolbar" aria-label="Områdesverktyg">
        {!drawing && !hasPolygon && (
          <button
            type="button"
            className="geofence-hud-button primary"
            onClick={startDrawing}
          >
            <span className="btn-icon" aria-hidden>✎</span>
            Starta ritning
          </button>
        )}

        {drawing && (
          <>
            <button
              type="button"
              className="geofence-hud-button"
              onClick={undoLastPoint}
              disabled={vertexCount === 0}
              title="Ångra senaste punkt"
            >
              <span className="btn-icon" aria-hidden>↶</span>
              Ångra punkt
            </button>
            <div className="geofence-hud-divider" />
            <button
              type="button"
              className="geofence-hud-button primary"
              onClick={finishDrawing}
              disabled={!canFinish}
              title={canFinish ? "Avsluta polygonen" : "Minst 3 punkter krävs"}
            >
              <span className="btn-icon" aria-hidden>✓</span>
              Avsluta ritning
            </button>
            <div className="geofence-hud-divider" />
            <button
              type="button"
              className="geofence-hud-button ghost-danger"
              onClick={cancelDrawing}
            >
              Avbryt
            </button>
          </>
        )}

        {!drawing && hasPolygon && (
          <>
            <button
              type="button"
              className="geofence-hud-button"
              onClick={startDrawing}
            >
              <span className="btn-icon" aria-hidden>✎</span>
              Rita om
            </button>
            <div className="geofence-hud-divider" />
            <button
              type="button"
              className="geofence-hud-button ghost-danger"
              onClick={clearPolygon}
            >
              <span className="btn-icon" aria-hidden>✕</span>
              Ta bort område
            </button>
          </>
        )}
      </div>
    </div>
  );
}
